// TODO unit tests
// TODO image storyshots

/**
 * If you're trying to figure out how to add a new SVG icon:
 *  - 1. Add your SVG to the './svgs/src' folder in this component
 *  - 2. Run `npm run generate-icons`, this will:
 * 		- Minify/optimise your SVG file
 * 		- Convert fill/stroke colors to 'currentColor'
 *  	- Rewrite all 'fill-rule' instances to 'fillRule' so it's React-friendly
 * 		- Update the auto-generated SVG Icon Component file a.k.a. './svgs/index.ts'
 *
 * Each icon name stems from its original '.svg' file name
 *
 * The icons are sourced from the Material Design Icon library
 * https://material.io/resources/icons/?style=baseline
 *
 * The SVG files have been adjusted to ensure that the icon artwork fills the SVG viewbox.
 */

import React from 'react';
import { titleCase } from 'change-case';

import { RAW_SVGS, REACT_SVGS, ICONS } from './svgs';
import * as S from './Icon.style';

// ------------------------------------

/**
 * Support CSS-in-JS icon rendering, where icon colour must
 * be explicitly provided (as it cannot be inferred/inherited).
 *
 * e.g. on a pseudo element: `background(-image): ${cssIcon(ICONS.PEOPLE, 'blue')};`
 */
export const cssIcon = (svg: ICONS, color: string) =>
	`url('data:image/svg+xml,${RAW_SVGS[svg].replace(
		/currentColor/g,
		encodeURIComponent(color),
	)}')`;

// ------------------------------------

export interface IProps {
	/** Name of icon from autogenerated ICONS map. */
	name: ICONS;
	/** CSS class name */
	className?: string;
	/** Rotate icon by degrees. */
	rotate?: number;
	/** Reverse Icon using CSS transform. */
	flip?: boolean;
	/** Override default width/height size in pixels. */
	size?: number;
	/** Override default HTML Styled Component tag <i/> containing the icon. */
	as?: any;
	/** Inline styles to apply to the tag containing the icon. */
	style?: { [key: string]: string };
	/** Tells assistive screen reader that icon is purely a decorative. */
	ariaHidden?: boolean;
	/** Tells assistive screen reader that icon is not focusable (download icons etc) i.e prevent tab focus */
	focusable?: boolean;
}

const Icon = ({
	as,
	name,
	rotate,
	flip,
	size = 20,
	style,
	className,
	ariaHidden,
	focusable,
}: IProps) => {
	// The ReactComponent representing the specified SVG
	const Icon = REACT_SVGS[name];

	return (
		!!Icon && (
			<S.Icon
				{...{
					as,
					rotate,
					flip,
					size,
					style,
					className,
					'aria-hidden': ariaHidden,
				}}
			>
				<Icon {...{ focusable }} />
				<span className="vh">{`'${titleCase(name)}' Icon`}</span>
			</S.Icon>
		)
	);
};

export { Icon, ICONS };
